import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, ExternalLink, Star, Navigation } from 'lucide-react';
import { trackEvent } from '../lib/ga';

const FindUs = () => {
  const [settings, setSettings] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, [API_URL]);

  // Directory listings - where to get listed
  const directoryListings = [
    {
      name: 'Google Business Profile',
      description: 'Most important for Google Maps & Search',
      url: 'https://business.google.com/',
      icon: '🔍',
      status: 'claim',
      priority: 'high'
    },
    {
      name: 'Yelp',
      description: 'Popular review platform for restaurants',
      url: 'https://biz.yelp.com/',
      icon: '⭐',
      status: 'claim',
      priority: 'high'
    },
    {
      name: 'TripAdvisor',
      description: 'Travel & dining reviews',
      url: 'https://www.tripadvisor.com/Owners',
      icon: '🦉',
      status: 'claim',
      priority: 'high'
    },
    {
      name: 'Mineral Wells Chamber of Commerce',
      description: 'Official local business directory',
      url: 'https://business.mineralwellstx.com/',
      icon: '🏛️',
      status: 'claim',
      priority: 'high'
    },
    {
      name: 'Parker County Chamber',
      description: 'Regional business directory',
      url: 'https://business.parkercountychamber.com/',
      icon: '🏛️',
      status: 'claim',
      priority: 'medium'
    },
    {
      name: 'Facebook Business',
      description: 'Social media presence',
      url: 'https://www.facebook.com/profile.php?id=61559570946841',
      icon: '📘',
      status: 'active',
      priority: 'high'
    },
    {
      name: 'Instagram',
      description: 'Photo sharing & engagement',
      url: 'https://www.instagram.com/1staveicp/',
      icon: '📷',
      status: 'active',
      priority: 'high'
    },
    {
      name: 'Apple Maps',
      description: 'Apple device users',
      url: 'https://mapsconnect.apple.com/',
      icon: '🍎',
      status: 'claim',
      priority: 'medium'
    },
    {
      name: 'Bing Places',
      description: 'Microsoft search engine',
      url: 'https://www.bingplaces.com/',
      icon: '🔷',
      status: 'claim',
      priority: 'medium'
    },
    {
      name: 'Foursquare',
      description: 'Location-based discovery',
      url: 'https://business.foursquare.com/',
      icon: '📍',
      status: 'claim',
      priority: 'low'
    },
    {
      name: 'Manta',
      description: 'Business directory & reviews',
      url: 'https://www.manta.com/',
      icon: '🐋',
      status: 'claim',
      priority: 'low'
    }
  ];

  // Cities we serve
  const citiesServed = [
    { name: 'Mineral Wells', distance: 'Downtown', slug: 'ice-cream-mineral-wells-tx' },
    { name: 'Weatherford', distance: '18 miles', slug: 'ice-cream-weatherford-tx' },
    { name: 'Azle', distance: '25 miles', slug: 'ice-cream-azle-tx' },
    { name: 'Aledo', distance: '28 miles', slug: 'ice-cream-aledo-tx' },
    { name: 'Willow Park', distance: '22 miles', slug: 'ice-cream-willow-park-tx' },
    { name: 'Springtown', distance: '20 miles', slug: 'ice-cream-springtown-tx' },
    { name: 'Graford', distance: '15 miles', slug: 'ice-cream-graford-tx' }
  ];

  return (
    <>
      <Helmet>
        <title>Find Us | 1st Ave ICP - Directions & Location | Mineral Wells, TX</title>
        <meta name="description" content="Find 1st Ave ICP ice cream shop in Mineral Wells, TX. Get directions, hours, contact info, and see us on Google Maps. Located at 103 NE 1st Ave, across from Baker Hotel." />
        <link rel="canonical" href="https://1staveicp.com/find-us" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative pt-28 pb-16 md:pt-36 md:pb-24 px-4"
        style={{
          background: 'linear-gradient(135deg, #E91E8C 0%, #C71585 50%, #9B1B6E 100%)'
        }}
      >
        <div className="max-w-6xl mx-auto text-center">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Find Us
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto">
            Visit us in the heart of downtown Mineral Wells, directly across from the historic Baker Hotel
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          
          {/* Location & Map Grid */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            
            {/* Contact Info Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                📍 Location & Contact
              </h2>
              
              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Address</h3>
                    <p className="text-gray-600">103 NE 1st Avenue</p>
                    <p className="text-gray-600">Mineral Wells, TX 76067</p>
                    <a 
                      href="https://www.google.com/maps/dir/?api=1&destination=103+NE+1st+Ave,+Mineral+Wells,+TX+76067"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('directions_click', { source: 'find_us_address' })}
                      className="inline-flex items-center gap-2 mt-2 text-pink-600 hover:text-pink-700 font-semibold"
                    >
                      <Navigation className="w-4 h-4" />
                      Get Directions
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Phone</h3>
                    <a 
                      href="tel:+18178294878"
                      onClick={() => trackEvent('phone_click', { source: 'find_us' })}
                      className="text-pink-600 hover:text-pink-700 font-semibold text-lg"
                    >
                      (817) 829-4878
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Email</h3>
                    <a 
                      href="mailto:1staveicp@gmail.com"
                      className="text-pink-600 hover:text-pink-700"
                    >
                      1staveicp@gmail.com
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-pink-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Hours</h3>
                    <div className="text-gray-600 space-y-1">
                      <p><span className="font-medium">Mon:</span> {settings?.hours_mon || 'Closed'}</p>
                      <p><span className="font-medium">Tue:</span> {settings?.hours_tue || '2:00 PM - 8:00 PM'}</p>
                      <p><span className="font-medium">Wed:</span> {settings?.hours_wed || '2:00 PM - 9:00 PM'}</p>
                      <p><span className="font-medium">Thu:</span> {settings?.hours_thu || '2:00 PM - 8:00 PM'}</p>
                      <p><span className="font-medium">Fri:</span> {settings?.hours_fri || '12:00 PM - 9:00 PM'}</p>
                      <p><span className="font-medium">Sat:</span> {settings?.hours_sat || '12:00 PM - 9:00 PM'}</p>
                      <p><span className="font-medium">Sun:</span> {settings?.hours_sun || '12:00 PM - 8:00 PM'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Embed */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <iframe
                title="1st Ave ICP Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3344.5!2d-98.1128!3d32.8084!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8653a6e7f0d8e0e9%3A0x1234567890abcdef!2s103%20NE%201st%20Ave%2C%20Mineral%20Wells%2C%20TX%2076067!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '400px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Cities We Serve */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-12">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              🚗 Cities We Serve
            </h2>
            <p className="text-gray-600 mb-6">
              We proudly serve ice cream lovers from Mineral Wells and surrounding communities in Palo Pinto and Parker Counties.
            </p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {citiesServed.map((city) => (
                <Link
                  key={city.slug}
                  to={`/${city.slug}`}
                  className="flex items-center gap-3 p-4 bg-pink-50 hover:bg-pink-100 rounded-xl transition-colors group"
                >
                  <span className="text-2xl">📍</span>
                  <div>
                    <p className="font-bold text-gray-900 group-hover:text-pink-600">{city.name}, TX</p>
                    <p className="text-sm text-gray-500">{city.distance}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Directory Listings */}
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
            <h2 
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              📋 Find Us Online
            </h2>
            <p className="text-gray-600 mb-6">
              Follow us on social media and find us on popular business directories:
            </p>
            
            {/* Active Listings */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Active Profiles
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {directoryListings.filter(d => d.status === 'active').map((directory) => (
                  <a
                    key={directory.name}
                    href={directory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors group border border-green-200"
                  >
                    <span className="text-3xl">{directory.icon}</span>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 group-hover:text-green-600 flex items-center gap-2">
                        {directory.name}
                        <ExternalLink className="w-4 h-4" />
                      </p>
                      <p className="text-sm text-gray-500">{directory.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* High Priority Directories */}
            <div className="mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Top Business Directories
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {directoryListings.filter(d => d.status === 'claim' && d.priority === 'high').map((directory) => (
                  <a
                    key={directory.name}
                    href={directory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-pink-50 rounded-xl transition-colors group border border-gray-200"
                  >
                    <span className="text-3xl">{directory.icon}</span>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 group-hover:text-pink-600 flex items-center gap-2">
                        {directory.name}
                        <ExternalLink className="w-4 h-4 opacity-50" />
                      </p>
                      <p className="text-sm text-gray-500">{directory.description}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Other Directories */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Other Directories</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {directoryListings.filter(d => d.status === 'claim' && d.priority !== 'high').map((directory) => (
                  <a
                    key={directory.name}
                    href={directory.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group text-sm"
                  >
                    <span className="text-xl">{directory.icon}</span>
                    <span className="font-medium text-gray-700 group-hover:text-pink-600">{directory.name}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <div 
              className="bg-gradient-to-r from-pink-500 to-pink-600 rounded-2xl p-8 text-white"
            >
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                Ready for Some Ice Cream? 🍦
              </h2>
              <p className="text-white/90 mb-6 max-w-xl mx-auto">
                Come visit us at 103 NE 1st Avenue in Mineral Wells. We're right across from the historic Baker Hotel!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=103+NE+1st+Ave,+Mineral+Wells,+TX+76067"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('directions_click', { source: 'find_us_cta' })}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-pink-600 font-bold rounded-full hover:bg-pink-50 transition-colors"
                >
                  <Navigation className="w-5 h-5" />
                  Get Directions
                </a>
                <Link
                  to="/menu"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-pink-700 text-white font-bold rounded-full hover:bg-pink-800 transition-colors"
                >
                  View Our Menu
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default FindUs;
